<template>
    <div class="min-h-screen bg-[#F6F8FA] font-sans text-slate-900">
        <header class="border-b border-slate-200 bg-white">
            <div class="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:px-6">
                <a href="/backstage" class="flex items-center gap-2.5 text-sm text-slate-500">
                    <img src="@/assets/logo-full.svg" alt="Heesify" class="h-8 w-auto brightness-0" />
                    <span class="border-l border-slate-200 pl-2.5">Audio ads</span>
                </a>
                <a href="/backstage/audio-ads/plans" class="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">Plans</a>
            </div>
        </header>

        <main class="mx-auto max-w-5xl px-4 py-8 sm:px-6">
            <h1 class="text-2xl font-semibold tracking-tight">Audio ads</h1>

            <div class="mt-4 flex flex-wrap gap-1 border-b border-slate-200" role="tablist" aria-label="Filter ads">
                <button
                    v-for="option in tabs"
                    :key="option.key"
                    type="button"
                    role="tab"
                    :aria-selected="tab === option.key"
                    class="-mb-px border-b-2 px-3 py-2 text-sm font-medium transition"
                    :class="tab === option.key ? 'border-slate-900 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'"
                    @click="tab = option.key"
                >
                    {{ option.label }}
                    <span class="ml-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">{{ inTab(option.key).length }}</span>
                </button>
            </div>

            <p v-if="error" role="alert" class="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{{ error }}</p>

            <p v-if="loaded && !visible.length" class="mt-6 rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">{{ emptyText }}</p>

            <div class="mt-6 space-y-4">
                <article v-for="ad in visible" :key="ad.id" class="rounded-xl border border-slate-200 bg-white p-5">
                    <div class="flex gap-4">
                        <img :src="ad.image_url" alt="" class="h-20 w-20 shrink-0 rounded-lg object-cover" />
                        <div class="min-w-0 flex-1">
                            <div class="flex flex-wrap items-center gap-2">
                                <h2 class="truncate font-semibold">{{ ad.title }}</h2>
                                <span class="rounded-md px-2 py-0.5 text-xs font-medium" :class="statusStyle(ad.status)">{{ statusLabel(ad.status) }}</span>
                                <span v-if="endingSoon(ad)" class="rounded-md bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">Ending soon</span>
                            </div>
                            <p class="mt-0.5 truncate text-sm text-slate-500">{{ ad.advertiser }} · {{ ad.email }}</p>
                            <p class="mt-0.5 text-sm text-slate-500">{{ ad.plan }} · ${{ ad.price }}<template v-if="ad.ends_at"> · ends {{ day(ad.ends_at) }}</template></p>
                            <a :href="ad.click_url" target="_blank" rel="noopener noreferrer" class="mt-0.5 block truncate text-sm text-slate-700 underline">{{ ad.click_url }}</a>
                        </div>
                    </div>

                    <audio :src="ad.audio_url" controls preload="none" class="mt-4 h-9 w-full"></audio>

                    <dl class="mt-4 grid grid-cols-3 gap-3 border-t border-slate-100 pt-4">
                        <div>
                            <dt class="text-xs text-slate-500">Plays</dt>
                            <dd class="mt-0.5 font-semibold">{{ ad.impressions.toLocaleString() }}<span class="text-sm font-normal text-slate-500"> / {{ ad.impression_limit.toLocaleString() }}</span></dd>
                            <dd v-if="ad.granted" class="text-xs text-slate-500">{{ ad.granted.toLocaleString() }} extra given</dd>
                        </div>
                        <div>
                            <dt class="text-xs text-slate-500">Learn more clicks</dt>
                            <dd class="mt-0.5 font-semibold">{{ ad.clicks.toLocaleString() }}</dd>
                        </div>
                        <div>
                            <dt class="text-xs text-slate-500">Click rate</dt>
                            <dd class="mt-0.5 font-semibold">{{ ad.click_rate }}%</dd>
                        </div>
                    </dl>

                    <div class="mt-4 border-t border-slate-100 pt-4">
                        <div class="flex flex-wrap items-center gap-1.5">
                            <span class="mr-1 text-xs text-slate-500">Audience</span>
                            <span v-for="item in audience(ad)" :key="item" class="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{{ item }}</span>
                            <button v-if="ad.countries.length || ad.genre_names.length" type="button" class="text-xs font-medium text-slate-600 underline" :aria-expanded="Boolean(open[ad.id])" @click="open[ad.id] = !open[ad.id]">{{ open[ad.id] ? "Hide" : "Show all" }}</button>
                        </div>
                        <div v-if="open[ad.id]" class="mt-3 max-h-48 space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
                            <p v-if="ad.countries.length"><span class="font-medium text-slate-900">Countries:</span> {{ ad.countries.join(", ") }}</p>
                            <p v-if="ad.genre_names.length"><span class="font-medium text-slate-900">Genres:</span> {{ ad.genre_names.join(", ") }}</p>
                        </div>
                    </div>

                    <p v-if="ad.review_note" class="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{{ ad.review_note }}</p>

                    <div v-if="ad.status === 'in_review'" class="mt-4 border-t border-slate-100 pt-4">
                        <div v-if="rejecting === ad.id" class="space-y-2">
                            <label class="block">
                                <span class="text-sm font-medium text-slate-700">Why can't this run? The sponsor will see this.</span>
                                <input v-model="notes[ad.id]" maxlength="300" class="mt-1.5 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
                            </label>
                            <div class="flex flex-wrap gap-2">
                                <button type="button" class="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" :disabled="busy || !notes[ad.id]" @click="act(ad, 'reject', { note: notes[ad.id] })">Reject and refund ${{ ad.price }}</button>
                                <button type="button" class="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700" @click="rejecting = null">Cancel</button>
                            </div>
                        </div>
                        <div v-else class="flex flex-wrap gap-2">
                            <button type="button" class="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60" :disabled="busy" @click="act(ad, 'approve', { version: ad.version })">Approve and start</button>
                            <button type="button" class="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" @click="rejecting = ad.id">Reject</button>
                        </div>
                    </div>

                    <div v-if="ad.running" class="mt-4 flex flex-wrap items-end justify-between gap-3 border-t border-slate-100 pt-4">
                        <div class="flex flex-wrap items-end gap-2">
                            <label class="block">
                                <span class="text-xs font-medium text-slate-600">Extra plays</span>
                                <input v-model.number="grants[ad.id + 'plays']" type="number" min="1" class="mt-1 block w-28 rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
                            </label>
                            <label v-if="ad.ends_at" class="block">
                                <span class="text-xs font-medium text-slate-600">Extra days</span>
                                <input v-model.number="grants[ad.id + 'days']" type="number" min="1" max="90" class="mt-1 block w-24 rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900" />
                            </label>
                            <button type="button" class="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50" :disabled="busy || !(grants[ad.id + 'plays'] || grants[ad.id + 'days'])" @click="give(ad)">Give extra</button>
                        </div>
                        <button v-if="ad.status === 'running'" type="button" class="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50" :disabled="busy" @click="act(ad, 'stop')">Stop ad</button>
                    </div>
                </article>
            </div>
        </main>
    </div>
</template>

<script>
import axios from "axios";

const API = "/api/backstage/audio-ads";

const STATUS = {
    awaiting_payment: ["Not paid yet", "bg-slate-100 text-slate-700"],
    in_review: ["In review", "bg-amber-100 text-amber-800"],
    running: ["Running", "bg-emerald-100 text-emerald-800"],
    finished: ["Finished", "bg-slate-100 text-slate-700"],
    rejected: ["Refunded", "bg-red-100 text-red-700"],
    stopped: ["Stopped", "bg-slate-100 text-slate-700"],
};

const count = (total, one, many) => `${total.toLocaleString()} ${total === 1 ? one : many}`;

export default {
    // A clean page of its own, like /sponsor, not the dark backstage layout
    layout: (_, page) => page,
    data() {
        return {
            ads: [],
            loaded: false,
            tab: "review",
            tabs: [
                { key: "review", label: "Needs review" },
                { key: "running", label: "Running" },
                { key: "all", label: "All" },
            ],
            notes: {},
            grants: {},
            open: {},
            rejecting: null,
            busy: false,
            error: null,
        };
    },
    computed: {
        visible() {
            return this.inTab(this.tab);
        },
        emptyText() {
            return { review: "Nothing to review.", running: "No ads are running.", all: "No ads yet." }[this.tab];
        },
    },
    async mounted() {
        await this.load();
        if (!this.inTab("review").length) this.tab = this.inTab("running").length ? "running" : "all";
    },
    methods: {
        inTab(key) {
            if (key === "review") return this.ads.filter((ad) => ad.status === "in_review");
            if (key === "running") return this.ads.filter((ad) => ad.running);
            return this.ads;
        },
        async load() {
            try {
                this.ads = (await axios.get(API)).data.ads;
            } catch (error) {
                this.error = "Couldn't load the ads. Please refresh.";
            }
            this.loaded = true;
        },
        statusLabel: (status) => STATUS[status]?.[0] ?? status,
        statusStyle: (status) => STATUS[status]?.[1] ?? "bg-slate-100 text-slate-700",
        day: (date) => new Date(date).toLocaleDateString(),
        audience(ad) {
            const parts = [];
            if (ad.countries.length) parts.push(count(ad.countries.length, "country", "countries"));
            if (ad.genders.length === 1) parts.push(ad.genders[0] === "female" ? "Women" : "Men");
            if (ad.ages.length) parts.push(ad.ages.length > 2 ? count(ad.ages.length, "age group", "age groups") : ad.ages.join(", "));
            if (ad.genre_names.length) parts.push(count(ad.genre_names.length, "genre", "genres"));
            return parts.length ? parts : ["Everyone"];
        },
        // Worth a look before they complain: nearly out of days or plays
        endingSoon(ad) {
            if (!ad.running) return false;
            const daysLeft = ad.ends_at ? (new Date(ad.ends_at) - Date.now()) / 86400000 : null;
            return (daysLeft !== null && daysLeft <= 5) || ad.impressions >= ad.impression_limit * 0.9;
        },
        async give(ad) {
            const data = { plays: this.grants[ad.id + "plays"] || null, days: this.grants[ad.id + "days"] || null };
            await this.act(ad, "grant", data);
            if (!this.error) {
                delete this.grants[ad.id + "plays"];
                delete this.grants[ad.id + "days"];
            }
        },
        async act(ad, action, data) {
            this.busy = true;
            this.error = null;
            try {
                await axios.post(`${API}/${ad.id}/${action}`, data);
                this.rejecting = null;
                await this.load();
            } catch (error) {
                await this.load();
                this.error = Object.values(error.response?.data?.errors ?? {})[0]?.[0] ?? "That didn't go through.";
            }
            this.busy = false;
        },
    },
};
</script>
